import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';

import { ClipBoardBlock, InstallerConfigForm } from 'components';
import {
  getInstallerExists,
  getInstallerIsLoading,
  getInstallerVersion,
  initialInstallerFetch,
  updateInstaller,
} from 'rdx/hardware/installer';
import { useScrollIntoView } from 'utils/hooks';

import { useInstallerDownloadLink } from '../hooks';

function Linux() {
  const dispatch = useDispatch();
  const { topic } = useParams();
  const ref = useScrollIntoView(topic);
  const installerExists = useSelector(getInstallerExists);
  const isLoading = useSelector(getInstallerIsLoading);
  const installerVersion = useSelector(getInstallerVersion);

  const downloadLink = useInstallerDownloadLink('Linux');

  return (
    <>
      <Card
        ref={topic === 'Installation' ? ref : null}
        className="installer-app-instructions mt-3 "
      >
        <Card.Header>
          Follow the few steps below to download and install the Installer
          application on your device.
        </Card.Header>
        <Card.Body>
          <ol>
            <li>
              Download the Installer app from <a href={downloadLink}>here.</a>
            </li>
            <li>
              Execute the downloaded file by double clicking on it. Your{' '}
              <em>Linux</em> software assistant will help you with the
              installation further on.
            </li>
            <li>
              Check if the installation is successful by pressing the Test
              Connection button below.
            </li>
          </ol>
          <Row
            // noGutters
            className="d-flex flex-wrap align-items-baseline"
          >
            <Col sm={12} md={3} className="mt-2 mt-md-0">
              <Button
                className="w-100"
                disabled={isLoading}
                onClick={() => dispatch(initialInstallerFetch())}
              >
                Test Connection
              </Button>
            </Col>
            <Col sm={12} md={3} className="mt-2 mt-md-0">
              <Button
                className="w-100"
                disabled={isLoading}
                variant="info"
                onClick={() => dispatch(updateInstaller())}
              >
                Update Installer
              </Button>
            </Col>
            <Col sm={12} md={6} className="mt-2 mt-md-0">
              <Alert
                className="text-center mt-sm-3"
                variant={installerExists ? 'success' : 'warning'}
              >
                {/* eslint-disable-next-line no-nested-ternary */}
                {isLoading ? (
                  <Spinner variant="warning" size="sm" animation="border" />
                ) : installerExists ? (
                  `Installer is running - ${installerVersion}`
                ) : (
                  'Installer not connected'
                )}
              </Alert>
            </Col>
          </Row>
          <p className="mt-3 text-right">
            <Button variant="link">
              <Link to="/hardware/installer/troubleshooting/Installation">
                Troubleshooting
              </Link>
            </Button>
          </p>
        </Card.Body>
      </Card>

      <Card className="mt-3" ref={topic === 'Customization' ? ref : null}>
        <Card.Header>Customize Installer App</Card.Header>
        <Card.Body>
          <Alert variant="warning">
            NB! Installer App is native application and the current
            configuration would only apply to the Installer on this machine.
          </Alert>
          <p>
            If you want to change the Installer App configurations, follow the
            steps below:
          </p>
          <ol>
            <li>Open terminal.</li>
            <li>
              Execute the following command:
              <ClipBoardBlock className="w-100 my-1">
                cd /usr/lib/Erply/installer2 && sudo nano config.json
              </ClipBoardBlock>
            </li>
            <li>
              Customize the configuration, save -{' '}
              <span className="bg-light">
                <code>Ctr + S</code>
              </span>{' '}
              and exit -{' '}
              <span className="bg-light">
                <code>Ctr + X</code>
              </span>
              .
            </li>
            <li>
              To apply the changes you would need to restart the Installer App.
              you can do this by executing the following command.
              <ClipBoardBlock className="w-100 my-1">
                cd /usr/lib/Erply/installer2 && sudo ./installer2_linux
                -service=stop && sudo ./installer2_linux -service=start
              </ClipBoardBlock>
            </li>
            <li>
              If you have changed the value for Installer{' '}
              <span className="bg-light">port</span>, do not forget to update
              the value in the listening port field below.
            </li>
          </ol>
        </Card.Body>
        <Card.Body>
          <InstallerConfigForm />
        </Card.Body>
      </Card>
    </>
  );
}

export default Linux;
